// Cobro con tarjeta por PASARELA — compartido por el POS y el POS Restaurant.
//
// PagoMedios devuelve una URL alojada (no una imagen): el número de tarjeta y el
// CVV se tecléan en la página del proveedor y **nunca pasan por este sistema**.
// Aquí se muestra esa URL como QR para que el cliente la escanee con su teléfono,
// y se sondea el estado hasta que el pago resuelve.
//
// El QR se dibuja en el navegador a partir de la URL: no hace falta pedirle una
// imagen al proveedor ni guardarla.
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { CheckCircle2, Copy, ExternalLink, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import {
  PASARELA_ACTIVO, type ModuloPos, type PasarelaCobro,
  pasarelaAnular, pasarelaCrearCobro, pasarelaEstado, money,
} from './shared';

/** Lo que la pasarela devuelve y la conciliación bancaria necesita: son los
 *  mismos campos que un cajero teclearía tras pasar la tarjeta por el datáfono. */
export interface DatosTarjeta {
  autorizacion: string | null; ultimos4: string | null; marca: string | null;
  lote: string | null; terminal: string | null; adquirente: string;
}

export function CobroPasarela({
  monto, modulo, cajaId, partnerId, moneda, medio = 'tarjeta', onPagado, onQrListo, onClose,
}: {
  monto: number;
  modulo: ModuloPos;
  cajaId: string | null;
  partnerId: string | null;
  moneda: string;
  /** 902 · Con qué se va a cobrar. Con 'deuna' el cliente cae directo en el QR
   *  de De Una, sin pasar por la pantalla de selección del proveedor. */
  medio?: 'tarjeta' | 'deuna';
  /** Se llama con la referencia del pago cuando la pasarela lo autoriza. */
  onPagado: (ref: string, extra: DatosTarjeta) => void;
  /** Espejo del QR en la pantalla de cliente: llega como imagen (data URI)
   *  porque el signage pinta un <img>, no una URL. Sólo lo usa el POS. */
  onQrListo?: (dataUrl: string) => void;
  onClose: () => void;
}) {
  const [cobro, setCobro] = useState<PasarelaCobro | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [intento, setIntento] = useState(0);   // reintentar tras vencer
  const espejoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let vivo = true;
    setCobro(null); setError(null);
    pasarelaCrearCobro(monto, modulo, cajaId, partnerId, medio)
      .then((c) => { if (vivo) setCobro(c); })
      .catch((e) => { if (vivo) setError(getErrorMessage(e)); });
    return () => { vivo = false; };
  }, [monto, modulo, cajaId, partnerId, medio, intento]);

  const activo = !!cobro && PASARELA_ACTIVO.includes(cobro.estado);
  const { data: st } = useQuery<PasarelaCobro>({
    queryKey: ['pos-pasarela-estado', cobro?.id ?? '', intento],
    enabled: activo,
    queryFn: () => pasarelaEstado(cobro!.id),
    refetchInterval: 2500, staleTime: 0,
  });

  const estado = st?.estado ?? cobro?.estado ?? 'pendiente';
  const url = st?.url ?? cobro?.url ?? null;
  const vencido = !!st?.vencido || estado === 'expirado';

  useEffect(() => {
    if (estado === 'autorizado' || estado === 'pagado') {
      const ref = st?.referencia_pago || cobro?.orden || '';
      toast.success(`Pago autorizado · ${ref}`);
      onPagado(ref, {
        autorizacion: st?.autorizacion ?? null,
        ultimos4: st?.ultimos4 ?? null,
        marca: st?.marca ?? null,
        lote: st?.lote ?? null,
        terminal: st?.tid ?? null,
        adquirente: st?.adquirente ?? st?.proveedor ?? 'pagomedios',
      });
    }
  }, [estado]);  // eslint-disable-line react-hooks/exhaustive-deps

  // El cliente no mira la pantalla del cajero: el mismo QR se espeja en la
  // pantalla que tiene de frente. Se envía como imagen porque el signage pinta
  // un <img>; el QR se dibuja aquí y nunca se le pide al proveedor.
  useEffect(() => {
    if (!url || !onQrListo) return;
    const c = espejoRef.current?.querySelector('canvas');
    if (c) onQrListo(c.toDataURL('image/png'));
  }, [url, onQrListo]);

  const cancelar = () => {
    // Sólo se anula lo que aún no se pagó: un cobro autorizado se devuelve con
    // un reverso en la pasarela, no cerrando esta ventana.
    if (cobro && activo) void pasarelaAnular(cobro.id).catch(() => undefined);
    onClose();
  };

  const copiar = () => {
    if (!url) return;
    void navigator.clipboard.writeText(url)
      .then(() => toast.success('Enlace copiado — puedes enviarlo por WhatsApp'))
      .catch(() => toast.error('No se pudo copiar el enlace'));
  };

  return (
    <div className="mx-auto w-full max-w-sm rounded-lg border bg-card p-5 text-center shadow-sm">
      <h3 className="mb-1 font-semibold">{medio === 'deuna' ? 'Cobro con De Una' : 'Cobro con tarjeta'}</h3>
      <p className="mb-3 text-2xl font-bold tabular-nums">{money(monto, moneda)}</p>

      {error && (
        <div className="space-y-3">
          <p className="text-sm text-red-600">{error}</p>
          <Button variant="outline" onClick={cancelar}>Cerrar</Button>
        </div>
      )}

      {!error && !cobro && <div className="py-10"><Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" /></div>}

      {!error && cobro && (estado === 'autorizado' || estado === 'pagado') && (
        <div className="space-y-2 py-6">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
          <p className="font-medium text-emerald-700 dark:text-emerald-400">Pago autorizado</p>
          {st?.marca && <p className="text-sm text-muted-foreground">{st.marca} ···· {st.ultimos4}</p>}
        </div>
      )}

      {!error && cobro && estado === 'rechazado' && (
        <div className="space-y-3 py-4">
          <XCircle className="mx-auto h-10 w-10 text-red-600" />
          <p className="text-sm text-red-600">{st?.error || 'La pasarela rechazó el pago'}</p>
          <Button size="sm" variant="outline" onClick={() => setIntento((n) => n + 1)}>Intentar de nuevo</Button>
        </div>
      )}

      {!error && cobro && estado === 'error' && (
        <div className="space-y-3 py-4">
          <XCircle className="mx-auto h-10 w-10 text-red-600" />
          <p className="text-sm text-red-600">{st?.error || cobro.error}</p>
          <Button size="sm" variant="outline" onClick={() => setIntento((n) => n + 1)}>Reintentar</Button>
        </div>
      )}

      {url && onQrListo && (
        <div ref={espejoRef} className="hidden" aria-hidden>
          <QRCodeCanvas value={url} size={420} level="M" />
        </div>
      )}

      {!error && cobro && PASARELA_ACTIVO.includes(estado) && !vencido && (
        <>
          <div className="mx-auto mb-3 flex h-56 w-56 items-center justify-center overflow-hidden rounded-md border bg-white p-2">
            {url
              ? <QRCodeSVG value={url} size={200} level="M" />
              : <Loader2 className="h-8 w-8 animate-spin text-primary" />}
          </div>
          {url ? (
            <>
              <p className="mb-2 text-xs text-muted-foreground">
                {medio === 'deuna'
                  ? 'El cliente escanea el código con su app De Una y confirma el pago.'
                  : 'El cliente escanea el código y paga en la página de la pasarela.'}
              </p>
              <div className="mb-3 flex justify-center gap-2">
                <Button size="sm" variant="outline" onClick={copiar}>
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copiar enlace
                </Button>
                <a href={url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm hover:bg-muted">
                  <ExternalLink className="h-3.5 w-3.5" /> Abrir
                </a>
              </div>
            </>
          ) : (
            <p className="mb-3 text-xs text-muted-foreground">Pidiendo el enlace a la pasarela…</p>
          )}
          <p className="mb-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Esperando el pago… ({cobro.orden})
          </p>
          <Button variant="outline" className="w-full" onClick={cancelar}>Cancelar cobro</Button>
        </>
      )}

      {!error && cobro && vencido && (
        <div className="space-y-3 py-4">
          <p className="text-sm font-medium text-amber-600">El cobro venció sin pago.</p>
          <Button size="sm" variant="outline" onClick={() => setIntento((n) => n + 1)}>Generar uno nuevo</Button>
          <Button variant="outline" className="w-full" onClick={cancelar}>Cerrar</Button>
        </div>
      )}
    </div>
  );
}
